// The merchant's price, held above the tab switch.
//
// A merchant sells a product once, at one price: the tab (VendorPricingTab)
// edits the catalog editor's `cost` — the tier code `EUR_1299`, the currency
// and the amount in cents — which the catalog save writes into the StorePrice
// the product's `price` points at (aldoo-com-api catalog/lib/product-price
// saveCost: created and linked when there is none). What the merchant types
// is kept here, so a half-typed price survives a tab change and the dialog's
// Save can refuse one that is not a price.
//
// It joins the dialog's Save with a section save of its own, registered once
// on mount: React runs this provider's effects before the catalog editor's
// (it renders inside it), so the price is checked before anything is written.
// The same save makes the product a one-time purchase: the store form takes
// `type: "purchase"` from it and writes it with the rest, without a word.
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react"
import { showNotification } from "aldoo-ra/Notification"
import { useStoreCurrency } from "aldoo-ra/Store/currency"
import useProductEditorTexts from "aldoo-ra/CMSModels/StoreProduct/useProductEditorTexts"
import { useVendorAccess } from "../../../AdminShared/VendorContext"
import { isCatalogProduct, useCatalogEditor } from "../shared/catalog-editor"

// the framework's one-time purchase (StoreProduct `type`)
export const ONE_TIME_PURCHASE = "purchase"
export const DEFAULT_CURRENCY = "EUR"
const CENTS = 100

const plain = (value) => (value && typeof value === "object" ? value.value : value)

/** The type the product is sold as ("purchase", "bundle", "subscription" …). */
export const productTypeOf = (product) => plain(product?.type) || ONE_TIME_PURCHASE

/** `EUR_1299` → { currency: "EUR", cents: 1299 }, or null. */
export const parseCost = (cost) => {
  const [currency, cents] = String(cost || "").split("_")
  if (!currency || cents === undefined || !/^\d+$/.test(cents)) return null
  return { currency: currency.toUpperCase(), cents: Number(cents) }
}

/** What a merchant typed ("12,5", "12.50") → cents, or null when it is not an amount of money. */
export const centsOf = (text) => {
  const normalized = String(text ?? "").trim().replace(",", ".")
  if (!/^\d+(\.\d{0,2})?$/.test(normalized)) return null
  return Math.round(Number(normalized) * CENTS)
}

/** Cents as the input shows them: 1299 → "12.99". */
export const amountText = (cents) => (Number.isFinite(cents) ? (cents / CENTS).toFixed(2) : "")

/** What is wrong with a typed price, or "" when it is one. */
export const priceError = (text, t) => {
  if (!String(text ?? "").trim()) return t`Enter a price greater than 0.`
  const cents = centsOf(text)
  if (cents === null) return t`Write the price as a number with up to two decimals, e.g. 12.99.`
  if (cents <= 0) return t`Enter a price greater than 0.`
  return ""
}

export const VendorPricingContext = createContext(null)

/** The merchant's price state, or null outside a merchant's dialog. */
export const useVendorPricing = () => useContext(VendorPricingContext)

/**
 * Mounted with the dialog (the extension's provider, inside the catalog
 * editor's). Only a merchant's catalog product has anything here: for an
 * admin the framework's Pricing tab stands, and the type is left alone.
 */
export function VendorPricingProvider({ dataItem, register, children }) {
  const { isAdmin, loading } = useVendorAccess()
  if (loading || isAdmin || !isCatalogProduct(dataItem)) return children
  return (
    <VendorPricingState dataItem={dataItem} register={register}>
      {children}
    </VendorPricingState>
  )
}

function VendorPricingState({ dataItem, register, children }) {
  const t = useProductEditorTexts()
  const catalog = useCatalogEditor()
  const editor = catalog?.ready ? catalog.editor : null
  const { currency: storeCurrency, currencies: storeCurrencies } = useStoreCurrency()

  const cost = editor ? parseCost(editor.read("cost")) : null
  // the price as saved: the editor holds nothing unsaved, so what it read is the stored one
  const [saved, setSaved] = useState(null)
  useEffect(() => {
    if (!editor || editor.dirty) return
    setSaved(parseCost(editor.read("cost")))
  }, [editor?.product, editor?.dirty])

  // what the merchant typed, until a save reloads the product (null: the editor's value)
  const [draft, setDraft] = useState(null)
  useEffect(() => {
    if (!editor || editor.dirty) return
    setDraft(null)
  }, [editor?.product])

  const currencies = useMemo(() => {
    const list = storeCurrencies?.length ? storeCurrencies : [DEFAULT_CURRENCY]
    // a price in a currency the store no longer lists stays pickable, so it is shown as it is
    return cost && !list.includes(cost.currency) ? [...list, cost.currency] : list
  }, [storeCurrencies, cost?.currency])
  const defaultCurrency = currencies.includes(DEFAULT_CURRENCY) ? DEFAULT_CURRENCY : storeCurrency || currencies[0]

  const amount = draft ? draft.amount : amountText(cost?.cents)
  const currency = draft?.currency || cost?.currency || defaultCurrency
  const error = draft ? priceError(draft.amount, t) : ""

  /** A typed amount or a picked currency: a price that is one goes to the editor, which saves it. */
  const change = (patch) => {
    const next = { amount, currency, ...patch }
    setDraft(next)
    const cents = centsOf(next.amount)
    if (!editor || priceError(next.amount, t)) return
    const code = `${next.currency}_${cents}`
    if (code === editor.read("cost")) return
    editor.set("cost", code)
  }

  const productType = productTypeOf(dataItem)
  // read by the save registered once, below
  const stateRef = useRef({})
  stateRef.current = { error, productType, t }

  useEffect(() => {
    register?.("vendor-pricing", {
      // never "dirty" itself: a price edit marks the catalog editor, and the type is not news
      save: async () => {
        const { error, productType, t } = stateRef.current
        if (error) {
          showNotification({ message: `${t`Pricing`}: ${error}`, className: "bg-admin_error text-white" })
          return false
        }
        // a merchant's product is a one-time purchase: the store form writes it with the rest
        if (productType !== ONE_TIME_PURCHASE) return { type: ONE_TIME_PURCHASE }
        return true
      },
    })
    return () => register?.("vendor-pricing", null)
  }, [register])

  const value = useMemo(
    () => ({ ready: !!editor, error: catalog?.error, amount, currency, currencies, saved, priceError: error, productType, change }),
    [editor, catalog?.error, amount, currency, currencies, saved, error, productType, cost?.cents]
  )
  return <VendorPricingContext.Provider value={value}>{children}</VendorPricingContext.Provider>
}
